import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Card, CardContent, TextField, MenuItem, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { reportingCapacityAPI } from '../services/reportingCapacityApi';

const num = (n) => (n == null ? '-' : Number(n).toLocaleString());
const gb = (g) => (g == null ? '-' : `${Number(g).toLocaleString(undefined, { maximumFractionDigits: 1 })} GB`);
const date = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');
const errText = (err, fallback) => err.response?.data?.detail || fallback;

export const severity = (pct) => (pct == null ? 'none' : pct >= 90 ? 'critical' : pct >= 75 ? 'warning' : 'ok');
const BAR = { ok: 'success.main', warning: 'warning.main', critical: 'error.main' };
const CHIP = { ok: ['On track', 'success'], warning: ['Warning', 'warning'], critical: ['Critical', 'error'], none: ['Not set', 'default'] };
const worst = (list) => list.filter((p) => p != null).sort((a, b) => b - a)[0] ?? null;

const SORTS = {
  sample_pct_desc: ['Samples % (high first)', (a, b) => (b.sample_pct ?? -1) - (a.sample_pct ?? -1)],
  test_pct_desc: ['Tests % (high first)', (a, b) => (b.test_pct ?? -1) - (a.test_pct ?? -1)],
  storage_pct_desc: ['Storage % (high first)', (a, b) => (b.storage_pct ?? -1) - (a.storage_pct ?? -1)],
  cust_name_asc: ['Customer A to Z', (a, b) => a.cust_name.localeCompare(b.cust_name)],
};

function Stat({ label, value, sub, color }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" component="div" color={color}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{sub}</Typography>
      </CardContent>
    </Card>
  );
}

// A consumption bar. The thin marker is how far through the licence year we are, so a bar past the marker is burning faster than time.
function Burn({ pct, periodPct }) {
  if (pct == null) return <Typography variant="caption" color="text.disabled">not configured</Typography>;
  const sev = severity(pct);
  return (
    <Box sx={{ minWidth: 120 }}>
      <Box role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${pct}% used`} sx={{ position: 'relative', height: 8, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(pct, 100)}%`, height: '100%', bgcolor: BAR[sev] }} />
        {periodPct != null && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.min(periodPct, 100)}%`, width: 2, bgcolor: 'text.primary', opacity: 0.4 }} />}
      </Box>
      <Typography variant="caption" sx={{ color: BAR[sev], fontWeight: 500 }}>{pct.toFixed(1)}%</Typography>
      {pct > 100 && <Typography variant="caption" color="error" sx={{ fontWeight: 600, ml: 1 }}>OVER LIMIT</Typography>}
    </Box>
  );
}

function Status({ pct }) {
  const [label, color] = CHIP[severity(pct)];
  return <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />;
}

function Amount({ used, limit, remaining, format = num }) {
  return (
    <Typography variant="caption" color="text.secondary" component="div">
      {format(used)}{limit ? ` / ${format(limit)}` : ''}{remaining != null && remaining >= 0 ? ` · ${format(remaining)} left` : ''}
    </Typography>
  );
}

function History({ code, currentYear }) {
  const [years, setYears] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    reportingCapacityAPI.burndownHistory(code)
      .then((res) => live && setYears(res.data.years))
      .catch((err) => live && setError(errText(err, 'Could not load history')));
    return () => { live = false; };
  }, [code]);
  const past = (years || []).filter((y) => y.year_number < currentYear).reverse();
  const cell = { bgcolor: 'action.hover' };
  if (error) return <TableRow><TableCell colSpan={6} sx={cell}><Alert severity="error">{error}</Alert></TableCell></TableRow>;
  if (!years) return <TableRow><TableCell colSpan={6} sx={cell}><Typography variant="caption" color="text.secondary">Loading history...</Typography></TableCell></TableRow>;
  if (!past.length) return <TableRow><TableCell colSpan={6} sx={cell}><Typography variant="caption" color="text.secondary">No previous contract years found.</Typography></TableCell></TableRow>;
  return past.map((y) => (
    <TableRow key={y.year_number} sx={{ '& td': cell }}>
      <TableCell sx={{ pl: 4 }}><Typography variant="caption" color="text.secondary">Year {y.year_number} (completed)</Typography></TableCell>
      <TableCell>
        <Typography variant="caption" component="div">{date(y.period_start)} to {date(y.period_end)}</Typography>
        {y.bundle_name && <Chip size="small" variant="outlined" label={y.bundle_name} sx={{ mt: 0.5 }} />}
      </TableCell>
      <TableCell><Burn pct={y.sample_pct} /><Amount used={y.used_samples} limit={y.sample_limit} /></TableCell>
      <TableCell><Burn pct={y.test_pct} /><Amount used={y.used_tests} limit={y.test_limit} /></TableCell>
      <TableCell><Typography variant="caption" color="text.disabled">n/a for past years</Typography></TableCell>
      <TableCell><Status pct={worst([y.sample_pct, y.test_pct])} /></TableCell>
    </TableRow>
  ));
}

export default function ReportingBurndown() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sort, setSort] = useState('sample_pct_desc');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let live = true;
    reportingCapacityAPI.burndown()
      .then((res) => live && setData(res.data))
      .catch((err) => live && setError(errText(err, 'Could not load the burn-down')));
    return () => { live = false; };
  }, []);

  const customers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.customers || []).filter((c) => !q || c.cust_name.toLowerCase().includes(q) || c.cust_code.toLowerCase().includes(q));
    return list.sort(SORTS[sort][1]);
  }, [data, sort, search]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Licence burn-down</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each active customer's consumption in the current licence year against its bundle and top-ups. Sample and test limits reset every anniversary; storage is cumulative.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {data && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Stat label="Critical (90% or more)" value={data.at_risk} sub="customers at or near a limit" color={data.at_risk > 0 ? 'error.main' : undefined} />
            <Stat label="Warning (75 to 89%)" value={data.warning} sub="approaching a limit" color={data.warning > 0 ? 'warning.main' : undefined} />
            <Stat label="Configured" value={`${data.configured} / ${data.total}`} sub={data.total > data.configured ? `${data.total - data.configured} without limits set` : 'all customers configured'} />
            <Stat label="As of" value={date(data.as_of)} sub="current licence year" />
          </Box>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" label="Search customer" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220 }} />
            <TextField select size="small" label="Sort by" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 230 }}>
              {Object.entries(SORTS).map(([k, [label]]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">{customers.length} customer{customers.length === 1 ? '' : 's'}</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: { md: 'auto' } }}>
              {[['ok', 'On track (under 75%)'], ['warning', 'Warning (75 to 89%)'], ['critical', 'Critical (90% or more)']].map(([k, label]) => (
                <Typography key={k} variant="caption"><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: BAR[k], mr: 0.5 }} />{label}</Typography>
              ))}
              <Typography variant="caption" color="text.secondary">Vertical line: share of the licence year elapsed</Typography>
            </Box>
          </Paper>
          <Paper sx={{ minWidth: 0 }}>
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader aria-label="Licence burn-down">
                <TableHead>
                  <TableRow><TableCell>Customer</TableCell><TableCell>Usage year</TableCell><TableCell sx={{ minWidth: 170 }}>Samples</TableCell><TableCell sx={{ minWidth: 170 }}>Tests</TableCell><TableCell sx={{ minWidth: 190 }}>Storage</TableCell><TableCell>Status</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((c) => {
                    const isOpen = open === c.cust_code;
                    return (
                      <React.Fragment key={c.cust_code}>
                        <TableRow hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.cust_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.cust_code} · {c.location || '-'}</Typography>
                            {c.current_year > 1 && (
                              <div><Button size="small" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : c.cust_code)}>{isOpen ? 'Hide history' : 'History'}</Button></div>
                            )}
                          </TableCell>
                          <TableCell>
                            {c.period_start ? (
                              <>
                                <Typography variant="caption" component="div">{date(c.period_start)} to {date(c.period_end)}</Typography>
                                <Typography variant="caption" color="text.secondary">Year {c.current_year}{c.contract_length_years ? ` of ${c.contract_length_years}` : ''} · {Math.round(c.period_pct)}% elapsed</Typography>
                              </>
                            ) : <Typography variant="caption" color="text.disabled">not configured</Typography>}
                          </TableCell>
                          <TableCell><Burn pct={c.sample_pct} periodPct={c.period_pct} /><Amount used={c.used_samples} limit={c.sample_limit} remaining={c.sample_remaining} /></TableCell>
                          <TableCell><Burn pct={c.test_pct} periodPct={c.period_pct} /><Amount used={c.used_tests} limit={c.test_limit} remaining={c.test_remaining} /></TableCell>
                          <TableCell>
                            <Burn pct={c.storage_pct} />
                            <Amount used={c.total_storage_gb} limit={c.storage_limit_gb} remaining={c.storage_remaining_gb} format={gb} />
                            {(c.volumes_gb > 0 || c.db_gb > 0) && <Typography variant="caption" color="text.disabled" component="div">Volumes: {gb(c.volumes_gb)} · DB: {gb(c.db_gb)}</Typography>}
                          </TableCell>
                          <TableCell><Status pct={worst([c.sample_pct, c.test_pct, c.storage_pct])} /></TableCell>
                        </TableRow>
                        {isOpen && <History code={c.cust_code} currentYear={c.current_year} />}
                      </React.Fragment>
                    );
                  })}
                  {customers.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No customers match your search.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Limits come from each customer's bundle and top-ups. Storage is the latest volume snapshot plus the latest database size report.
          </Typography>
        </>
      )}
    </Box>
  );
}
